import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { KpiCard, Skeleton, EmptyState } from "@/components/EmptyState";
import { PlanGate, featureAllowedByPlan } from "@/components/PlanGate";
import { api, extractErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { TrendingUp, Euro, Users, Zap, AlertTriangle, BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
  BarChart, Bar, LineChart, Line, Legend,
} from "recharts";

const tipStyle = {
  background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
  borderRadius: 8, color: "hsl(var(--popover-foreground))", fontSize: 12,
};

export default function Analytics() {
  const { company } = useAuth();
  const allowed = featureAllowedByPlan(company?.plan || "free_trial", "advanced_analytics");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const [val, forecast, vel, spend, turn] = await Promise.all([
        api.get("/analytics/valuation-trend?days=30"),
        api.get("/analytics/reorder-forecast"),
        api.get("/analytics/production-velocity?days=30"),
        api.get("/analytics/supplier-spend"),
        api.get("/analytics/stock-turnover?days=30"),
      ]);
      setData({
        val: val.data,
        forecast: forecast.data,
        vel: vel.data,
        spend: spend.data,
        turn: turn.data,
      });
    } catch (e) { toast.error(extractErr(e, "Failed to load analytics")); }
    setLoading(false);
  }, [allowed]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) {
    return <AppShell title="Advanced Analytics" subtitle="Enterprise analytics"><PlanGate feature="Advanced analytics" planRequired="Enterprise"><div className="h-48" /></PlanGate></AppShell>;
  }

  const latestVal = data.val?.trend?.slice(-1)?.[0]?.value || 0;
  const valChange = (() => {
    if (!data.val?.trend || data.val.trend.length < 2) return 0;
    const first = data.val.trend[0].value;
    const last = data.val.trend[data.val.trend.length - 1].value;
    return first ? ((last - first) / first) * 100 : 0;
  })();
  const totalVelocity = (data.vel?.velocity || []).reduce((a, b) => a + b.units, 0);
  const topSupplier = data.spend?.suppliers?.[0];

  return (
    <AppShell title="Advanced Analytics" subtitle="Trends, forecasts & insights (Enterprise)">
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Inventory value" value={`€${latestVal.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`} icon={Euro} hint={`${valChange >= 0 ? "+" : ""}${valChange.toFixed(1)}% in 30d`} />
            <KpiCard label="Units produced (30d)" value={totalVelocity.toLocaleString()} icon={Zap} accent="bg-primary/10 text-primary" />
            <KpiCard label="Reorder soon" value={data.forecast?.forecasts?.length || 0} icon={AlertTriangle} accent="bg-amber-500/10 text-amber-500" hint="Forecast horizon 14d" />
            <KpiCard label="Top supplier" value={topSupplier ? `€${topSupplier.total_spend.toLocaleString("en-GB", { maximumFractionDigits: 0 })}` : "—"} icon={Users} hint={topSupplier?.supplier || "No PO data yet"} />
          </div>

          <div className="mt-4 rounded-2xl border border-border bg-card p-4">
            <div className="font-heading font-semibold mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Inventory valuation trend (30d)</div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.val?.trend || []}>
                <defs>
                  <linearGradient id="opVal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.6} vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `€${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                <Tooltip contentStyle={tipStyle} formatter={(v) => [`€${Number(v).toLocaleString()}`, "Value"]} />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--chart-1))" fill="url(#opVal)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="font-heading font-semibold mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-primary" /> Production velocity</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.vel?.velocity || []}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.6} vertical={false} />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={tipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="units" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="orders" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="font-heading font-semibold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Supplier spend</div>
              {(data.spend?.suppliers?.length || 0) === 0 ? (
                <EmptyState className="border-0" title="No supplier spend yet" description="Receive POs to build supplier insights." />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.spend.suppliers.slice(0, 6)}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.6} vertical={false} />
                    <XAxis dataKey="supplier" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `€${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                    <Tooltip contentStyle={tipStyle} formatter={(v) => [`€${Number(v).toLocaleString()}`, "Spend"]} />
                    <Bar dataKey="total_spend" radius={[6, 6, 0, 0]} fill="hsl(var(--chart-1))" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-border bg-card">
            <div className="p-4 border-b border-border"><div className="font-heading font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Reorder forecast</div><div className="text-xs text-muted-foreground">Items estimated to need reordering soon (based on last 30 days of usage)</div></div>
            {(data.forecast?.forecasts?.length || 0) === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">All good — no urgent reorders forecast.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="px-4 py-2 text-left">SKU</th><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-right">Qty</th><th className="px-4 py-2 text-right">Min</th><th className="px-4 py-2 text-right">Avg/day</th><th className="px-4 py-2 text-right">Days to reorder</th><th className="px-4 py-2">Supplier</th></tr>
                  </thead>
                  <tbody>
                    {data.forecast.forecasts.map((f) => (
                      <tr key={f.item_id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2 font-mono text-xs">{f.sku}</td>
                        <td className="px-4 py-2">{f.name}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{f.qty}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{f.min_stock}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{f.avg_daily_usage}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {f.days_until_reorder !== null && (
                            <span className={f.days_until_reorder <= 0 ? "text-destructive font-medium" : f.days_until_reorder <= 7 ? "text-amber-500 font-medium" : ""}>
                              {f.days_until_reorder}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{f.supplier || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-2xl border border-border bg-card">
            <div className="p-4 border-b border-border"><div className="font-heading font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Stock turnover (30d)</div><div className="text-xs text-muted-foreground">High turnover = fast-moving items.</div></div>
            {(data.turn?.items?.length || 0) === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No outtake data yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="px-4 py-2 text-left">SKU</th><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-right">On hand</th><th className="px-4 py-2 text-right">Consumed</th><th className="px-4 py-2 text-right">Turnover ratio</th></tr>
                  </thead>
                  <tbody>
                    {data.turn.items.slice(0, 15).map((r) => (
                      <tr key={r.item_id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2 font-mono text-xs">{r.sku}</td>
                        <td className="px-4 py-2">{r.name}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.current_qty}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.consumed}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">{r.turnover_ratio}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </AppShell>
  );
}
